/**
 * KYB helpers shared by the moderator KYB pages (docs/decisions/kyb.md).
 *
 * The flow the UI walks a moderator through:
 *   1. registration — the company declared at least one entity registration;
 *   2. check       — a moderator ran a provider (recommended by jurisdiction);
 *   3. decision    — a moderator reviewed hits and confirmed (or dismissed) it;
 *   4. badge       — a moderator awarded a badge the evidence supports.
 * The contract gate and matching only read the resulting verdict.
 *
 * Status → chip-class maps live here; labels are i18n keys under `kyb:`.
 */
import type {
  CompanyBadgeAwardStateEnum,
  VerdictEnum,
  VerificationCheck,
  VerificationQueueRowStateEnum,
} from "@/services/moderators/generated/model";

export const QUEUE_STATES = [
  "needs_check",
  "in_progress",
  "awaiting_decision",
  "awaiting_badge",
  "verified",
] as const satisfies readonly VerificationQueueRowStateEnum[];

export const CHECK_STATUSES = ["pending", "running", "needs_selection", "clear", "review", "failed"] as const;
export type CheckStatusKey = (typeof CHECK_STATUSES)[number];

export const CHECK_DECISIONS = ["pending", "approved", "rejected", "dismissed"] as const;
export type CheckDecisionKey = (typeof CHECK_DECISIONS)[number];

/** Use cases a provider declares and a check reports as `covered`. */
export const USE_CASES = [
  "registry_lookup",
  "officers",
  "ubo",
  "aml_company",
  "aml_people",
  "documents",
  "monitoring",
] as const;
export type UseCaseKey = (typeof USE_CASES)[number];

export const ROUTE_ROLES = ["primary", "backup", "free", "manual"] as const;
export type RouteRoleKey = (typeof ROUTE_ROLES)[number];

export const FLOW_STEPS = ["registration", "check", "decision", "badge"] as const;
export type FlowStep = (typeof FLOW_STEPS)[number];
export type StepState = "done" | "current" | "todo";

const SUCCESS = "mod-page__chip--success";
const WARNING = "mod-page__chip--warning";
const DANGER = "mod-page__chip--danger";

export const VERDICT_CHIP: Record<VerdictEnum, string> = {
  verified: SUCCESS,
  pending: WARNING,
  rejected: DANGER,
  expired: WARNING,
  none: "",
};

export const STATE_CHIP: Record<VerificationQueueRowStateEnum, string> = {
  needs_check: "",
  in_progress: WARNING,
  awaiting_decision: WARNING,
  awaiting_badge: WARNING,
  verified: SUCCESS,
};

export const CHECK_STATUS_CHIP: Record<CheckStatusKey, string> = {
  pending: WARNING,
  running: WARNING,
  needs_selection: WARNING,
  clear: SUCCESS,
  review: WARNING,
  failed: DANGER,
};

export const DECISION_CHIP: Record<CheckDecisionKey, string> = {
  pending: WARNING,
  approved: SUCCESS,
  rejected: DANGER,
  dismissed: "",
};

export const AWARD_STATE_CHIP: Record<CompanyBadgeAwardStateEnum, string> = {
  active: SUCCESS,
  paused: WARNING,
  expired: WARNING,
  revoked: DANGER,
};

export const MATCH_CHIP: Record<string, string> = {
  same: SUCCESS,
  similar: WARNING,
  different: DANGER,
  not_in_registry: DANGER,
  unknown: "",
};

function pick<K extends string>(value: string | null | undefined, keys: readonly K[], fallback: K): K {
  return value && (keys as readonly string[]).includes(value) ? (value as K) : fallback;
}

export const checkStatusKey = (s: string | null | undefined): CheckStatusKey => pick(s, CHECK_STATUSES, "pending");
export const checkDecisionKey = (s: string | null | undefined): CheckDecisionKey => pick(s, CHECK_DECISIONS, "pending");
export const isUseCase = (s: string): s is UseCaseKey => (USE_CASES as readonly string[]).includes(s);
export const isRouteRole = (s: string | null | undefined): s is RouteRoleKey =>
  !!s && (ROUTE_ROLES as readonly string[]).includes(s);

/** A check is still with the provider (polling) and not yet decidable. */
export function isCheckInFlight(check: Pick<VerificationCheck, "status">): boolean {
  return check.status === "pending" || check.status === "running";
}

/** What `confirm` will record: approved only when the registry matched and no hit was confirmed. */
export function confirmOutcome(check: VerificationCheck): "approved" | "rejected" {
  if (check.kind === "monitoring") {
    return check.hits.some((h) => h.decision === "confirmed") ? "rejected" : "approved";
  }
  const registryMatched = check.registryResult?.status === "matched";
  const confirmedHit = check.hits.some((h) => h.decision === "confirmed");
  return registryMatched && !confirmedHit ? "approved" : "rejected";
}

export interface FlowInput {
  hasRegistration: boolean;
  checks: Pick<VerificationCheck, "kind" | "decision">[];
  hasPublicBadge: boolean;
}

/** Where a company is in the four-step KYB flow. The first unfinished step is `current`. */
export function flowStates({ hasRegistration, checks, hasPublicBadge }: FlowInput): Record<FlowStep, StepState> {
  const kyb = checks.filter((c) => c.kind === "kyb");
  const done: Record<FlowStep, boolean> = {
    registration: hasRegistration,
    check: kyb.length > 0,
    decision: kyb.some((c) => c.decision === "approved" || c.decision === "rejected"),
    badge: hasPublicBadge,
  };
  const out = {} as Record<FlowStep, StepState>;
  let currentSet = false;
  for (const step of FLOW_STEPS) {
    if (done[step]) out[step] = "done";
    else if (!currentSet) {
      out[step] = "current";
      currentSet = true;
    } else out[step] = "todo";
  }
  return out;
}

/** "CA-QC" / "AE-DMCC" when a subdivision is known, else the country. */
export function jurisdictionLabel(country: string, subdivision?: string | null): string {
  return subdivision?.trim() ? subdivision : country;
}
